import { useEffect, useRef, useState, useCallback } from 'react'
import { motion, AnimatePresence, useDragControls } from 'framer-motion'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'

const SNAP_POINTS = {
  FULL: { height: '95vh', label: 'full' },
  HALF: { height: '50vh', label: 'half' },
  MINIMIZED: { height: '0vh', label: 'minimized' },
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  initialSnapPoint = 'half',
  className,
}) {
  const [snapPoint, setSnapPoint] = useState(initialSnapPoint)
  const dragControls = useDragControls()
  const sheetRef = useRef(null)
  const isDraggingRef = useRef(false)

  // Reset to initial position when opened
  useEffect(() => {
    if (isOpen) {
      setSnapPoint(initialSnapPoint)
    }
  }, [isOpen, initialSnapPoint])

  const handleDragEnd = useCallback(
    (event, info) => {
      // Small delay to ensure click doesn't fire right after drag
      setTimeout(() => {
        isDraggingRef.current = false
      }, 50)

      const velocity = info.velocity.y
      const offset = info.offset.y

      // Determine snap point based on drag velocity and offset
      // Significant downward movement: Close or go to half
      if (velocity > 400 || offset > 120) {
        if (snapPoint === 'full') {
          setSnapPoint('half')
        } else {
          onClose()
        }
      }
      // Significant upward movement: Go to full
      else if (velocity < -400 || offset < -120) {
        if (snapPoint === 'half') {
          setSnapPoint('full')
        }
      }
      // Default: snap back to current point
      else {
        setSnapPoint(snapPoint)
      }
    },
    [snapPoint, onClose],
  )

  const currentHeight = SNAP_POINTS[snapPoint.toUpperCase()]?.height || SNAP_POINTS.HALF.height

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className={cn(
              'fixed inset-0 bg-background/80 backdrop-blur-sm z-[200]',
              isOpen ? 'pointer-events-auto' : 'pointer-events-none',
            )}
          />

          {/* Bottom Sheet */}
          <motion.div
            ref={sheetRef}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
            }}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.1, bottom: 0.3 }}
            onDragEnd={handleDragEnd}
            style={{
              height: currentHeight,
              touchAction: 'none',
            }}
            className={cn(
              'fixed bottom-0 left-0 right-0 z-[201]',
              'bg-background border-t border-border/10 rounded-t-2xl shadow-2xl',
              'flex flex-col overflow-hidden',
              isOpen ? 'pointer-events-auto' : 'pointer-events-none',
              className,
            )}
          >
            {/* Drag Handle Area */}
            <div
              className="w-full pt-3 pb-1 flex flex-col items-center cursor-grab active:cursor-grabbing select-none"
              onPointerDown={(e) => {
                isDraggingRef.current = false
                dragControls.start(e)
              }}
              onDragStart={() => {
                isDraggingRef.current = true
              }}
              onClick={() => {
                // If it was a drag, don't trigger click behaviors
                if (isDraggingRef.current) return

                // Single click on the handle area expands if half, or returns to half if full
                if (snapPoint === 'half') {
                  setSnapPoint('full')
                } else if (snapPoint === 'full') {
                  setSnapPoint('half')
                }
              }}
            >
              <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full mb-2" />
              {title && (
                <div className="w-full px-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold">{title}</h2>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onClose()
                    }}
                    className="p-2 hover:bg-muted rounded-full transition-colors pointer-events-auto"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
